import gsap from 'gsap';
import { safeNum } from './dot-cms-aniamtion.helper';
import { AnimationDynamicSettings, AnimationType } from './animation/animations.model';


export const mouseAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts?: gsap.TweenVars) => gsap.core.Tween>
> = {
  'mouse-tilt3d': (s, el) => {
    const htmlEl = el as HTMLElement;
    const angle = safeNum(s.angle, 15);
    const perspective = safeNum(s.perspective, 800);
    const ease = s.easing && s.easing.startsWith('ease') ? 'power1.out' : s.easing ?? 'power1.inOut';
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const invert = s.direction?.toLowerCase().includes('invert');

    htmlEl.style.transformStyle = 'preserve-3d';
    htmlEl.style.transformOrigin = '50% 50%';

    const handler = (e: PointerEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      const dx = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const dy = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      let rotationX = -dy * angle;
      let rotationY = dx * angle;

      if (invert) {
        rotationX = -rotationX;
        rotationY = -rotationY;
      }

      gsap.to(htmlEl, {
        rotationX,
        rotationY,
        transformPerspective: perspective,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    window.addEventListener('pointermove', handler);
    const tween = gsap.to(htmlEl, { duration: 0 });
    (tween as any).cleanup = () => window.removeEventListener('pointermove', handler);
    return tween;
  },

  'mouse-track': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 50);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const ease = s.easing ?? 'power1.inOut';
    const invert = s.direction?.toLowerCase().includes('invert');
    const axis = s.axis ?? 'both';

    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      const dx = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const dy = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      const factor = invert ? -1 : 1;

      const x = axis === 'x' || axis === 'both' ? dx * distance * factor : 0;
      const y = axis === 'y' || axis === 'both' ? dy * distance * factor : 0;

      gsap.to(htmlEl, {
        x,
        y,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    window.addEventListener('mousemove', handler);
    const tween = gsap.to({}, { duration: 0 });
    (tween as any).cleanup = () => window.removeEventListener('mousemove', handler);
    return tween;
  },

  'mouse-blob': (s, el) => {
    const htmlEl = el as HTMLElement;
    const pointerDistance = safeNum(s.distance, 80);
    const pointerVelocity = safeNum(s.pointerVelocity, 1);
    const scale = safeNum(s.scale, 1);
    const ease = s.easing ?? 'power1.inOut';

    htmlEl.style.transformOrigin = '50% 50%';
    htmlEl.style.transform = `scale(${scale})`;

    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      let dx = e.clientX - (rect.left + rect.width / 2);
      let dy = e.clientY - (rect.top + rect.height / 2);
      const dist = Math.hypot(dx, dy);
      if (dist > pointerDistance) {
        const ratio = pointerDistance / dist;
        dx *= ratio;
        dy *= ratio;
      }
      gsap.to(htmlEl, {
        x: dx,
        y: dy,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    window.addEventListener('mousemove', handler);
    const tween = gsap.to(htmlEl, { duration: 0 });
    (tween as any).cleanup = () => window.removeEventListener('mousemove', handler);
    return tween;
  },

  'mouse-obscure': (s, el) => {
    const htmlEl = el as HTMLElement;
    const pointerDistance = safeNum(s.distance, 80);
    const pointerVelocity = safeNum(s.pointerVelocity, 1);
    const scale = safeNum(s.scale, 1);
    const angle = safeNum(s.angle, 0);
    const blur = safeNum(s.blur, 0);
    const ease = s.easing ?? 'power1.inOut';

    htmlEl.style.transformOrigin = '50% 50%';
    htmlEl.style.transform = `scale(${scale})`;

    const handler = (e: PointerEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      let dx = e.clientX - (rect.left + rect.width / 2);
      let dy = e.clientY - (rect.top + rect.height / 2);
      const dist = Math.hypot(dx, dy);
      if (dist > pointerDistance) {
        const ratio = pointerDistance / dist;
        dx *= ratio;
        dy *= ratio;
      }
      const rad = (angle * Math.PI) / 180;
      const rotatedX = dx * Math.cos(rad) - dy * Math.sin(rad);
      const rotatedY = dx * Math.sin(rad) + dy * Math.cos(rad);
      const blurValue = (dist / pointerDistance) * blur;

      gsap.to(htmlEl, {
        x: rotatedX,
        y: rotatedY,
        rotation: angle,
        filter: `blur(${blurValue}px)`,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    window.addEventListener('pointermove', handler);
    const tween = gsap.to(htmlEl, { duration: 0 });
    (tween as any).cleanup = () => window.removeEventListener('pointermove', handler);
    return tween;
  },

  'mouse-track3d': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 80);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const angle = safeNum(s.angle, 15);
    const perspective = safeNum(s.perspective, 800);
    const ease = s.easing?.startsWith('ease') ? 'power1.out' : s.easing ?? 'power1.inOut';
    const axis = s.axis ?? 'both';

    htmlEl.style.transformStyle = 'preserve-3d';
    htmlEl.style.transformOrigin = '50% 50%';

    const handler = (e: PointerEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      let dx = e.clientX - (rect.left + rect.width / 2);
      let dy = e.clientY - (rect.top + rect.height / 2);
      const dist = Math.hypot(dx, dy);
      if (dist > distance) {
        const ratio = distance / dist;
        dx *= ratio;
        dy *= ratio;
      }
      const normX = dx / (rect.width / 2);
      const normY = dy / (rect.height / 2);
      const rotationX = axis === 'y' ? 0 : -normY * angle;
      const rotationY = axis === 'x' ? 0 : normX * angle;

      gsap.to(htmlEl, {
        rotationX,
        rotationY,
        transformPerspective: perspective,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    window.addEventListener('pointermove', handler);
    const tween = gsap.to(htmlEl, { duration: 0 });
    (tween as any).cleanup = () => window.removeEventListener('pointermove', handler);
    return tween;
  },

  'mouse-airy': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 100);
    const pointerVelocity = safeNum(s.pointerVelocity, 1.5);
    const angle = safeNum(s.angle, 0);
    const ease = s.easing ?? 'power1.inOut';
    const axis = s.axis ?? 'both';

    htmlEl.style.transformStyle = 'preserve-3d';
    htmlEl.style.transformOrigin = '50% 50%';

    // ✅ Use MouseEvent instead of PointerEvent for consistency
    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      let dx = e.clientX - (rect.left + rect.width / 2);
      let dy = e.clientY - (rect.top + rect.height / 2);

      const dist = Math.hypot(dx, dy);
      if (dist > distance) {
        const ratio = distance / dist;
        dx *= ratio;
        dy *= ratio;
      }

      const rad = (angle * Math.PI) / 180;
      const rotatedX = dx * Math.cos(rad) - dy * Math.sin(rad);
      const rotatedY = dx * Math.sin(rad) + dy * Math.cos(rad);

      const rotationX = axis === 'y' ? 0 : -rotatedY / 10;
      const rotationY = axis === 'x' ? 0 : rotatedX / 10;
      const moveX = axis === 'y' ? 0 : rotatedX / 15;
      const moveY = axis === 'x' ? 0 : rotatedY / 15;

      gsap.to(htmlEl, {
        x: moveX,
        y: moveY,
        rotationX,
        rotationY,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    // ✅ Use mousemove instead of pointermove
    window.addEventListener('mousemove', handler);
    const tween = gsap.to(htmlEl, { duration: 0, x: 0, y: 0, rotationX: 0, rotationY: 0 });
    (tween as any).cleanup = () => window.removeEventListener('mousemove', handler);
    return tween;
  },

  'mouse-resize': (s, el) => {
    const htmlEl = el as HTMLElement;
    const distance = safeNum(s.distance, 100);
    const scale = safeNum(s.scale, 1.5);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const ease = s.easing ?? 'power1.inOut';

    htmlEl.style.transformOrigin = '50% 50%';

    // ✅ Use MouseEvent for consistency
    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      const dx = e.clientX - (rect.left + rect.width / 2);
      const dy = e.clientY - (rect.top + rect.height / 2);
      const dist = Math.hypot(dx, dy);

      // ✅ Calculate proximity correctly (0 = far, 1 = close)
      const proximity = Math.max(0, 1 - dist / distance);
      const currentScale = 1 + (scale - 1) * proximity;

      gsap.to(htmlEl, {
        scale: currentScale,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    // ✅ Use mousemove instead of pointermove
    window.addEventListener('mousemove', handler);
    const tween = gsap.to(htmlEl, { duration: 0, scale: 1 });
    (tween as any).cleanup = () => window.removeEventListener('mousemove', handler);
    return tween;
  },

  'mouse-skew3d': (s, el) => {
    const htmlEl = el as HTMLElement;
    // const distance = safeNum(s.distance, 100);
    const skew = safeNum(s.skew, 20);
    const pointerVelocity = safeNum(s.pointerVelocity, 0.3);
    const ease = s.easing ?? 'power1.inOut';

    htmlEl.style.transformOrigin = '50% 50%';
    htmlEl.style.transformStyle = 'preserve-3d';

    // ✅ Use MouseEvent for consistency
    const handler = (e: MouseEvent) => {
      const rect = htmlEl.getBoundingClientRect();
      const dx = e.clientX - (rect.left + rect.width / 2);
      const dy = e.clientY - (rect.top + rect.height / 2);

      // ✅ Normalize to -1..1 range properly
      const normX = Math.max(-1, Math.min(1, dx / (rect.width / 2)));
      const normY = Math.max(-1, Math.min(1, dy / (rect.height / 2)));

      const skewX = normX * skew;
      const skewY = normY * skew;

      gsap.to(htmlEl, {
        skewX,
        skewY,
        ease,
        duration: pointerVelocity,
        overwrite: 'auto',
      });
    };

    // ✅ Use mousemove instead of pointermove
    window.addEventListener('mousemove', handler);
    const tween = gsap.to(htmlEl, { duration: 0, skewX: 0, skewY: 0 });
    (tween as any).cleanup = () => window.removeEventListener('mousemove', handler);
    return tween;
  },
};
